<script lang="ts">
	import calendarIcon from "@core/assets/icons/calendar.svg?raw";
	import couchIcon from "@core/assets/icons/couch.svg?raw";
	import type { Procedure } from "@core/schema/Procedure.js";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { getProcedureDetailPath } from "@core/schema/paths/getProcedureDetailPath.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import Formatted from "../common/Formatted.svelte";
	import Link from "../common/Link.svelte";
	import MediaWithHoverVideo from "../common/MediaWithHoverVideo.svelte";
	import { ButtonVariant } from "../common/getButtonClass.js";

	interface Props {
		featuredProcedures: Procedure[];
	}

	const { featuredProcedures }: Props = $props();
	const { tenant } = getGlobalTemplateProps();
	const { FILE_BUCKET_URL_MAP } = getGlobalSettings();

	function getVideoUrl(video: unknown): string | undefined {
		const file = video as UploadedFile | undefined;
		if (!file) {
			return undefined;
		}
		return getUploadedFilePath(file, FILE_BUCKET_URL_MAP);
	}

	function formatDuration(minutes: number): string {
		return `(${minutes} min)`;
	}

	function formatPrice(price: number): string {
		return `${price.toLocaleString("cs-CZ")} Kč`;
	}

	const allProcedures = $derived.by((): (Procedure & { isMainFeatured: boolean })[] => {
		if (featuredProcedures.length >= 3) {
			return [
				{ ...featuredProcedures[1], isMainFeatured: false },
				{ ...featuredProcedures[0], isMainFeatured: true },
				{ ...featuredProcedures[2], isMainFeatured: false },
			] as (Procedure & { isMainFeatured: boolean })[];
		}
		return featuredProcedures.map((proc, index) => ({
			...proc,
			isMainFeatured: index === 0,
		}));
	});

	// Store refs to video components
	interface VideoComponent {
		play(): void;
		stop(): void;
	}
	const videoRefs: Record<number, VideoComponent | undefined> = {};

	// Handle hover on desktop
	function handleCardMouseEnter(index: number): void {
		const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
		const ref = videoRefs[index];
		if (!isMobile && ref !== undefined) {
			ref.play();
		}
	}

	function handleCardMouseLeave(index: number): void {
		const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
		const ref = videoRefs[index];
		if (!isMobile && ref !== undefined) {
			ref.stop();
		}
	}

	function getFirstImageUrl(images: unknown[]): string | null {
		if (images.length > 0) {
			return getUploadedFilePath(images[0] as UploadedFile, FILE_BUCKET_URL_MAP);
		}
		return null;
	}
</script>

<section class="full-width">
	<div class="container-center">
		<h2 class="sr-only">Naše procedury</h2>
		<div class="flex flex-col lg:flex-row gap-4 justify-center items-center px-4 lg:px-0">
			{#each allProcedures as procedure, index (procedure.id)}
				{@const imageUrl = getFirstImageUrl(procedure.images)}
				{@const videoUrl = getVideoUrl(procedure.video)}
				<div
					class="flex flex-col items-center {procedure.isMainFeatured
						? 'pt-4 pb-6 px-4 md:pt-4 md:pb-16 md:px-4 gap-4 md:gap-10 w-full md:max-w-[28rem] lg:min-w-[20rem] lg:max-w-[20rem] xl:min-w-[26.25rem] xl:max-w-[26.25rem] bg-hazelnut-400 text-ivory-100 shadow-[0px_16px_16px_-16px_rgba(0,0,0,0.1)]'
						: 'pt-4 pb-6 px-4 md:pt-4 md:pb-16 md:px-4 gap-4 md:gap-6 w-full md:max-w-[28rem] lg:min-w-[17.5rem] lg:max-w-[17.5rem] xl:min-w-[22.875rem] xl:max-w-[22.875rem] bg-ivory-100 text-hazelnut-500 border border-ivory-300 shadow-[0px_24px_8px_-16px_rgba(0,0,0,0.05)]'} rounded-[2rem] md:rounded-[4rem]"
					role="group"
					onmouseenter={(): void => {
						handleCardMouseEnter(index);
					}}
					onmouseleave={(): void => {
						handleCardMouseLeave(index);
					}}
				>
					<a
						href={getProcedureDetailPath(tenant.locale, procedure.urlSlug)}
						aria-label={procedure.title}
						class="block {procedure.isMainFeatured
							? 'w-full h-[13rem] md:h-[20rem] xl:h-[24.25rem]'
							: 'w-full h-[12rem] md:h-[17.5rem] xl:h-[20.875rem]'} bg-transparent rounded-[1rem] md:rounded-[3rem] overflow-hidden flex-none self-stretch"
					>
						<MediaWithHoverVideo
							bind:this={videoRefs[index]}
							imageSrc={imageUrl ?? ""}
							videoSrc={videoUrl}
							alt={procedure.title}
							imageWidth={procedure.isMainFeatured ? 500 : 380}
							imageHeight={procedure.isMainFeatured ? 500 : 380}
							class="w-full h-full object-cover"
							wrapperClass="w-full h-full"
							playOnScroll={true}
							eager
						/>
					</a>
					<div
						class="flex flex-col items-center {procedure.isMainFeatured
							? 'gap-3 md:gap-10'
							: 'gap-3 md:gap-8'} flex-none self-stretch"
					>
						<h3
							class="font-heading text-base md:text-[2rem] font-bold text-center leading-[1.1] md:leading-[110%] flex-none self-stretch {procedure.isMainFeatured
								? 'text-[#F7F3EF]'
								: 'text-[#4F372F]'}"
						>
							<a href={getProcedureDetailPath(tenant.locale, procedure.urlSlug)} class="no-underline hover:underline">
								<Formatted text={procedure.title} />
								<br />
								<span class="inline-block">
									{formatDuration(procedure.duration)}
								</span>
							</a>
						</h3>

						<p
							class="text-xs md:text-[1rem] md:leading-[140%] font-medium text-center flex-none self-stretch {procedure.isMainFeatured
								? 'text-[#F8F8F7]'
								: 'text-[#21212F]'}"
						>
							<Formatted text={procedure.description} />
						</p>
						<div
							class="flex flex-wrap justify-center items-center content-center gap-1 md:gap-1 w-full md:max-w-[20.625rem] flex-none"
						>
							{#each procedure.tags.slice(0, 8) as tag, tagIndex (tag)}
								<span
									class="justify-center items-center px-2 md:px-3 py-0.5 md:py-1 rounded-[2rem] md:rounded-[2.5rem] text-[10px] md:text-xs font-medium leading-[0.875rem] md:leading-[0.9375rem] uppercase border-[1px] md:border-[1.5px] border-[#F7CFCF] flex-none {procedure.isMainFeatured
										? 'text-[#F7F3EF]'
										: 'text-[#1C1F2B]'} {tagIndex >= 5 ? 'hidden lg:flex' : 'flex'}"
								>
									<Formatted text={tag} />
								</span>
							{/each}
							{#if procedure.tags.length > 5}
								<span
									class="flex lg:hidden justify-center items-center px-2 py-0.5 rounded-[2rem] text-[10px] font-medium leading-[0.875rem] border-[1px] border-[#F7CFCF] flex-none {procedure.isMainFeatured
										? 'text-[#F7F3EF]'
										: 'text-[#1C1F2B]'}"
								>
									a další…
								</span>
							{/if}
							{#if procedure.tags.length > 8}
								<span
									class="hidden lg:flex justify-center items-center md:px-3 md:py-1 md:rounded-[2.5rem] md:text-xs font-medium md:leading-[0.9375rem] md:border-[1.5px] border-[#F7CFCF] flex-none {procedure.isMainFeatured
										? 'text-[#F7F3EF]'
										: 'text-[#1C1F2B]'}"
								>
									a další…
								</span>
							{/if}
						</div>

						<div class="flex flex-col items-center gap-2 md:gap-2 flex-none self-stretch">
							<p
								class="text-sm md:text-[1.5rem] md:leading-[2.25rem] font-bold text-center flex-none {procedure.isMainFeatured
									? 'text-[#F7F3EF]'
									: 'text-[#21212F]'}"
							>
								{formatPrice(procedure.price)}
							</p>
							<Link
								href="https://head-spa-na-pohodu.reservio.com/"
								target="_blank"
								variant={ButtonVariant.PrimarySmall}
								iconStart={calendarIcon}
							>
								rezervovat termín
							</Link>
						</div>
					</div>
				</div>
			{/each}
		</div>

		<div class="flex justify-center mt-8 md:mt-12">
			<Link href="/procedury" iconStart={couchIcon} variant={ButtonVariant.SecondaryMedium}
				>zobrazit všechny procedury</Link
			>
		</div>
	</div>
</section>
